import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { Bounds, SpatialObject } from '@/types';
import { useEngine } from '@/app/EngineContext';
import { useCameraStore } from '@/store/cameraStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useSettingsStore } from '@/store/settingsStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { computeMinimapModel, minimapToWorld, worldToMinimap, type MinimapModel } from '@/core/rendering/minimapModel';
import { centerCameraOn } from '@/core/camera/cameraOps';
import styles from './Minimap.module.css';

const BOX = { width: 220, height: 150 };
const MAX_CONNECTIONS = 300;

const fillFor = (o: SpatialObject, selected: boolean, css: CSSStyleDeclaration): string => {
  if (selected) return css.getPropertyValue('--focus').trim() || '#b5522a';
  switch (o.kind) {
    case 'group':
    case 'folder':
      return css.getPropertyValue('--hover-bg').trim() || 'rgba(60, 52, 40, 0.1)';
    case 'window':
      return css.getPropertyValue('--note-sky').trim() || '#c9d9e3';
    case 'note':
      return css.getPropertyValue(`--note-${o.color}`).trim() || '#f9f6ef';
    default:
      return css.getPropertyValue('--surface').trim() || '#f9f6ef';
  }
};

/**
 * Live overview drawn on one canvas: objects, containers, connections (when few) and
 * the viewport. Redraws through the frame scheduler on camera or document change.
 * Click or drag to move the camera to that world point; scroll wheel is left to the
 * canvas so users can still zoom with the pointer over the map.
 */
export const Minimap = () => {
  const visible = useSettingsStore((s) => s.minimapVisible);
  const breakpoint = useBreakpoint();
  return visible && breakpoint !== 'compact' ? <MinimapCanvas /> : null;
};

const MinimapCanvas = () => {
  const { scene, frames, animator } = useEngine();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modelRef = useRef<MinimapModel | null>(null);
  const dragging = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = BOX.width * dpr;
    canvas.height = BOX.height * dpr;
    const draw = () => {
      // Read tokens per draw so a colour-scheme change is picked up without remounting.
      const css = getComputedStyle(canvas);
      const ink = css.getPropertyValue('--ink-faint').trim() || '#8d8578';
      const edge = css.getPropertyValue('--surface-edge').trim() || 'rgba(31,28,24,0.14)';
      const focus = css.getPropertyValue('--focus').trim() || '#b5522a';
      const { camera, viewport } = useCameraStore.getState();
      const doc = useWorkspaceStore.getState().workspaces[useWorkspaceStore.getState().activeWorkspaceId];
      const selected = new Set(useSelectionStore.getState().selection.objectIds);
      const model = computeMinimapModel(scene.contentBounds(), camera, viewport.size, BOX);
      modelRef.current = model;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, BOX.width, BOX.height);
      if (!doc) return;

      const rect = (b: Bounds) => {
        const m = worldToMinimap(model, b);
        return { x: m.minX, y: m.minY, w: Math.max(1, m.maxX - m.minX), h: Math.max(1, m.maxY - m.minY) };
      };

      // Containers first (behind), then everything else by z.
      const ids = scene.ids().filter((id) => scene.isInteractive(id));
      const objs = ids.map((id) => doc.objects[id]).filter((o): o is SpatialObject => !!o);
      objs.sort(
        (a, b) =>
          Number(!(a.kind === 'group' || a.kind === 'folder')) - Number(!(b.kind === 'group' || b.kind === 'folder')) ||
          a.zIndex - b.zIndex,
      );

      const connections = Object.values(doc.connections);
      if (connections.length <= MAX_CONNECTIONS) {
        ctx.strokeStyle = ink;
        ctx.globalAlpha = 0.6;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (const c of connections) {
          const a = scene.boundsOf(c.sourceId);
          const b = scene.boundsOf(c.targetId);
          if (!a || !b) continue;
          const ra = rect(a);
          const rb = rect(b);
          ctx.moveTo(ra.x + ra.w / 2, ra.y + ra.h / 2);
          ctx.lineTo(rb.x + rb.w / 2, rb.y + rb.h / 2);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      for (const o of objs) {
        const b = scene.boundsOf(o.id);
        if (!b) continue;
        const r = rect(b);
        const isContainer = o.kind === 'group' || o.kind === 'folder';
        ctx.fillStyle = fillFor(o, selected.has(o.id), css);
        ctx.strokeStyle = selected.has(o.id) ? focus : edge;
        ctx.lineWidth = selected.has(o.id) ? 1.5 : 1;
        if (isContainer) ctx.setLineDash([2, 2]);
        ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w, r.h);
        if (isContainer) ctx.setLineDash([]);
      }

      const v = model.viewport;
      ctx.strokeStyle = focus;
      ctx.lineWidth = 1.5;
      ctx.fillStyle = css.getPropertyValue('--focus-soft').trim() || 'rgba(181, 82, 42, 0.18)';
      ctx.globalAlpha = 0.5;
      ctx.fillRect(v.minX, v.minY, v.maxX - v.minX, v.maxY - v.minY);
      ctx.globalAlpha = 1;
      ctx.strokeRect(v.minX + 0.5, v.minY + 0.5, v.maxX - v.minX, v.maxY - v.minY);
    };

    draw();
    const schedule = () => {
      frames.schedule(draw);
    };
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', schedule);
    const unsubs = [
      () => {
        media.removeEventListener('change', schedule);
      },
      useCameraStore.subscribe(schedule),
      useWorkspaceStore.subscribe((s, prev) => {
        if (s.revision !== prev.revision || s.activeWorkspaceId !== prev.activeWorkspaceId) schedule();
      }),
      useSelectionStore.subscribe((s, prev) => {
        if (s.selection !== prev.selection) schedule();
      }),
    ];
    return () => {
      for (const u of unsubs) u();
    };
  }, [scene, frames]);

  const navigate = (e: ReactPointerEvent<HTMLCanvasElement>, animate: boolean) => {
    const model = modelRef.current;
    if (!model) return;
    const box = e.currentTarget.getBoundingClientRect();
    const world = minimapToWorld(model, { x: e.clientX - box.left, y: e.clientY - box.top });
    const { camera, viewport } = useCameraStore.getState();
    const target = centerCameraOn(world, viewport.size, camera.zoom);
    if (animate) animator.to(target);
    else {
      animator.cancel();
      useCameraStore.getState().setCamera(target);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      className={styles.minimap}
      style={{ width: BOX.width, height: BOX.height }}
      data-testid="minimap"
      aria-label="Minimap"
      role="img"
      onPointerDown={(e) => {
        e.stopPropagation();
        if (e.button !== 0) return;
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        navigate(e, true);
      }}
      onPointerMove={(e) => {
        if (dragging.current) navigate(e, false);
      }}
      onPointerUp={(e) => {
        dragging.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
    />
  );
};
